import { Box, Button, Checkbox, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { handleSave } from "../utils/handleSave";
import { TABLE_SCROLL_CONTAINER_SX } from "../utils/formHelpers";
import { createTempId } from "../utils/createTempId";
import { toast } from "react-toastify";
import { useKotFormDirty } from "../utils/useKotFormDirty";
import Tiptap from "../components/RichTextEditor/RichTextEdition";
import { canModerateAdditionalInfoComments } from "../utils/additionalInfoPermissions";

const isRichTextEmpty = (html) => {
  if (!html) return true;
  const stripped = html.replace(/<[^>]*>/g, "").trim();
  return stripped.length === 0;
};

const AdditionalInfo = ({ mode, users, userRolesMap }) => {
  const isView = mode === "view";
  const savedData = useSelector((state) => state.kotClient.additionalInfo);
  const clientId = useSelector((state) => state.kotClient?.clientId);
  const currentUserId = localStorage.getItem("TENANT_USER_ID") || "";

  // PPS, FFS, US Patent Paralegal and Admin moderate every comment in this section;
  // everyone else stays limited to the ones they wrote. Empty userRolesMap (the roles
  // request failed) falls back to own-comments-only.
  const canModerateComments = canModerateAdditionalInfoComments(userRolesMap?.[currentUserId]);

  // A row may be edited, selected and deleted when it is an unsaved draft, your own
  // comment, or - for the moderator roles above - anybody's comment.
  const canManageRow = (row) => canModerateComments || !row.isExisting || row.userId === currentUserId;

  const getUserInfo = (userId) => {
    const userObj = users.find((u) => u.personid?.toString() === userId);
    const firstName = userObj?.first_name || "";
    const lastName = userObj?.last_name || "";
    const role = userRolesMap[userId]?.[0] || "";
    return `${firstName} ${lastName} (${role})`.trim();
  };


  const buildRows = (nextRows = []) =>
    nextRows.length > 0
      ? nextRows.map((row) => {
          return {
            ...row,
            isExisting: true,
            user: getUserInfo(row.userId),
          };
        })
      : [
          {
            id: createTempId(),
            userId: currentUserId || "",
            updatedOn: "",
            comment: "",
            isExisting: false,
            user: getUserInfo(currentUserId),
          },
        ];

  const [rows, setRows] = useState(buildRows(savedData));

  const [isSaving, setIsSaving] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const { isDirty, markDirty, markSaved, markReset, syncClean } = useKotFormDirty();

  const dispatch = useDispatch();

  useEffect(() => {
    setRows(buildRows(savedData));
    syncClean();
  }, [clientId, savedData]);

  const saveData = async () => {
    setIsSaving(true);
    try {
      const updatedRows = rows
        .filter((row) => row.isExisting || !isRichTextEmpty(row.comment))
        .map((row) => ({
          id: row.id,
          userId: row.userId,
          updatedOn: row.updatedOn,
          comment: isRichTextEmpty(row.comment) ? "" : row.comment,
        }));
      const result = await handleSave(dispatch, "additionalInfo", updatedRows, clientId, savedData);
      if (result?.status !== "error") {
        markSaved();
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    markReset();
    setRows(buildRows(savedData));
    setSelectedRows([]);
  };

  const updateRowField = (id, field, value) => {
    setRows((prev) => {
      let changed = false;

      const updated = prev.map((row) => {
        if (row.id !== id) {
          return row;
        }

        if (row[field] === value) {
          return row;
        }

        changed = true;

        return {
          ...row,
          [field]: value,
        };
      });

      if (changed) {
        markDirty();
      }

      return updated;
    });
  };

  const addRow = () => {
    // Comment is the mandatory field here; require it on the new row before adding another.
    const hasIncompleteOwnRow = rows.some((row) => row.userId === currentUserId && !row.isExisting && isRichTextEmpty(row.comment));

    if (hasIncompleteOwnRow) {
      toast.warn("Complete the mandatory comment before adding another.");
      return;
    }

    markDirty();
    setRows((prev) => [
      ...prev,
      {
        id: createTempId(),
        userId: currentUserId || "",
        updatedOn: "",
        comment: "",
        isExisting: false,
        user: getUserInfo(currentUserId),
      },
    ]);
  };

  const handleSelectRow = (id) => {
    const row = rows.find((r) => r.id === id);

    if (!row || !canManageRow(row)) return;

    setSelectedRows((prev) => (prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]));
  };

  const deleteSelectedRows = () => {
    markDirty();
    setRows((prev) => prev.filter((row) => !selectedRows.includes(row.id) || !canManageRow(row)));

    setSelectedRows([]);
  };

  const manageableRowIds = rows.filter((row) => canManageRow(row)).map((row) => row.id);
  return (
    <Box p={4} bgcolor="#fff" borderRadius={2}>
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h3" mb={3}>
          Additional Information
        </Typography>
        {!isView && selectedRows.length > 0 && (
          <Button variant="contained" color="error" onClick={deleteSelectedRows}>
            Delete ({selectedRows.length})
          </Button>
        )}
      </Box>

      <Box sx={TABLE_SCROLL_CONTAINER_SX}>
        <Table sx={{ minWidth: 600, tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox data-ignore-dirty="true" disabled={isView || manageableRowIds.length === 0} checked={selectedRows.length === manageableRowIds.length && manageableRowIds.length > 0} indeterminate={selectedRows.length > 0 && selectedRows.length < manageableRowIds.length} onChange={(e) => setSelectedRows(e.target.checked ? manageableRowIds : [])} size="medium" />
              </TableCell>
              <TableCell>Comments</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell padding="checkbox">{canManageRow(row) && !isView ? <Checkbox data-ignore-dirty="true" checked={selectedRows.includes(row.id)} onChange={() => handleSelectRow(row.id)} size="medium" /> : null}</TableCell>
                <TableCell sx={{ verticalAlign: "top" }}>
                  <Box
                    sx={{
                      backgroundColor: row.isExisting && !canManageRow(row) ? "#f5f5f5" : "#fff",
                      borderRadius: 1,

                      "& .MuiPaper-root": {
                        boxShadow: "none",
                      },

                      "& .MuiToggleButton-root": {
                        p: "4px",
                      },
                    }}
                  >
                    <Tiptap content={row.comment || ""} editable={!isView && canManageRow(row)} minHeight={120} onChange={(html) => updateRowField(row.id, "comment", html)} />
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      {!isView && (
        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Button variant="outlined" onClick={addRow}>
            Add More
          </Button>
        </Box>
      )}

      {!isView && (
        <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
          <Button variant="text" onClick={handleCancel} disabled={isSaving || !isDirty}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveData} disabled={isSaving || !isDirty}>
            Save
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default AdditionalInfo;
